import { images } from '@/asset';
import BaseModal from '@/components/BaseModal';
import BaseTable from '@/components/BaseTable';
import useToast from '@/hooks/useToast';
import useUserPlan from '@/hooks/useUserPlan';
import { apiCaller } from '@/redux/query';
import { isSkipApiSelector } from '@/redux/slice/auth.slice';
import languageRuleSlice from '@/redux/slice/languageRule.slice';
import { IPagination } from '@/types';
import { IResponseApi } from '@/types/apis/response';
import { Button, Badge, Icon, IndexTable, useIndexResourceState, Box } from '@shopify/polaris';
import { DeleteIcon, EditIcon } from '@shopify/polaris-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

interface IProps {
  onEmptyAction: () => void;
  onEdit: (id: number) => void;
}

const Table = ({ onEmptyAction, onEdit }: IProps) => {
  const { show } = useToast();
  const dispatch = useDispatch();
  const { isFreePlan } = useUserPlan();
  const isSkip = useSelector(isSkipApiSelector);
  const [pagination, setPagination] = useState<IPagination>({ page: 1, perPage: 5 });
  const { data: dataResponse, isLoading: isLoadingData } = apiCaller.useGetLanguageRulesQuery(pagination, { skip: isSkip });
  const [deleteLanguageRule, { isLoading: isLoadingDelete }] = apiCaller.useRemoveLanguageRuleMutation();
  const [deleteMultipleLanguageRule, { isLoading: isLoadingDeleteMultiple }] = apiCaller.useBulkRemoveLanguageRuleMutation();
  const [updateMultipleLanguageRule, { isLoading: isLoadingUpdateMultiple }] = apiCaller.useBulkUpdateLanguageRuleMutation();
  const [selectedRuleId, setSelectedRuleId] = useState<number>(-1);
  const [isShowModalDelete, setIsShowModalDelete] = useState<boolean>(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dataRules = useMemo(() => dataResponse?.data, [JSON.stringify(dataResponse?.data)]);

  useEffect(() => {
    dataRules && dispatch(languageRuleSlice.actions.handleSetRules(dataRules));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(dataRules), dispatch]);

  const { selectedResources, allResourcesSelected, handleSelectionChange, clearSelection } = useIndexResourceState(
    dataRules ? (dataRules?.map((data) => ({ ...data, id: data?.id ? data?.id.toString() : '' })) as any) : [],
  );

  const handleDelete = async () => {
    const isDeleteMultiple = selectedResources.length > 0 && selectedRuleId === -1;
    if (!selectedRuleId && !isDeleteMultiple) return;
    let res: IResponseApi.ICommon;
    try {
      if (isDeleteMultiple) {
        res = await deleteMultipleLanguageRule(selectedResources.join(',')).unwrap();
      } else {
        res = await deleteLanguageRule(+selectedRuleId).unwrap();
      }
      if (res.status === 'success') {
        show(isDeleteMultiple ? 'Rules deleted' : 'Rule deleted');
      }

      setIsShowModalDelete(false);
      clearSelection();
    } catch {}
  };

  const handleBulkUpdate = useCallback(
    async (status: boolean) => {
      let res: IResponseApi.ICommon;
      try {
        res = await updateMultipleLanguageRule({ ids: selectedResources?.toString(), active: status }).unwrap();
        if (res.status === 'success') {
          show('Updated');
        }
      } catch {}
    },
    [selectedResources, show, updateMultipleLanguageRule],
  );

  const actionItems = useMemo(() => {
    return [
      {
        content: 'Activate',
        onAction: () => {
          handleBulkUpdate(true);
        },
        loading: isLoadingUpdateMultiple,
      },
      {
        content: 'Deactivate',
        onAction: () => {
          handleBulkUpdate(false);
        },
        loading: isLoadingUpdateMultiple,
      },
      {
        content: <Icon source={DeleteIcon} />,
        onAction: () => {
          setSelectedRuleId(-1);
          setIsShowModalDelete(true);
        },
        loading: false,
      },
    ];
  }, [handleBulkUpdate, isLoadingUpdateMultiple]);

  const rowMarkup = useMemo(() => {
    return dataRules?.map(({ id, nameLanguage, active, ruleContents }, index) => {
      return (
        <IndexTable.Row
          id={id + ''}
          key={id}
          selected={selectedResources.includes(id + '')}
          position={index}
          onClick={() => {}} //empty fn to prevent select checkbox when click row
        >
          <IndexTable.Cell>{`${nameLanguage}`}</IndexTable.Cell>

          <IndexTable.Cell>
            <div
              onClick={() => {
                handleSelectionChange('single' as any, !selectedResources.includes(id + ''), id + '');
              }}
              className="d-inline-block cursor-pointer"
            >
              <Badge tone={active ? 'success' : undefined}>{active ? 'Active' : 'Inactive'}</Badge>
            </div>
          </IndexTable.Cell>
          <IndexTable.Cell>
            <div className="text-center">
              <Button
                disabled={false}
                icon={EditIcon}
                onClick={() => {
                  onEdit(id as number);
                }}
                variant="plain"
              />
              <Button
                disabled={false}
                icon={DeleteIcon}
                onClick={() => {
                  setSelectedRuleId(id as number);
                  setIsShowModalDelete(true);
                }}
                variant="plain"
              />
            </div>
          </IndexTable.Cell>
        </IndexTable.Row>
      );
    });
  }, [dataRules, onEdit, selectedResources, handleSelectionChange]);

  return (
    <>
      <BaseTable
        isLoading={isLoadingData}
        isFetching={false}
        columnContentTypes={['text', 'text', 'text']}
        headings={[{ title: 'Language' }, { title: 'Status' }, { title: '' }]}
        data={dataRules || []}
        rows={rowMarkup || []}
        actionItems={actionItems}
        totalPage={dataResponse?.meta?.pagination?.total_pages || 0}
        totalItem={dataResponse?.meta?.pagination?.total || 0}
        pagination={pagination}
        setPagination={(pagination: IPagination) => {
          setPagination(pagination);
        }}
        selectedResources={selectedResources}
        allResourcesSelected={allResourcesSelected}
        handleSelectionChange={handleSelectionChange}
        imgEmptySource={images.emptyTranslation}
        titleEmpty={'No language versions available.'}
        emptyAction={onEmptyAction}
        titleActionBtnEmpty={'Add language'}
        isSearching={false}
        disabledBtnEmpty={isFreePlan}
      />
      <BaseModal
        title={'Delete this delivery method rule?'}
        open={isShowModalDelete}
        onClose={() => setIsShowModalDelete(false)}
        primaryAction={{
          content: 'Delete',
          onAction: handleDelete,
          loading: isLoadingDelete || isLoadingDeleteMultiple,
          id: 'btn-destructive-tone',
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setIsShowModalDelete(false),
          },
        ]}
        children={
          <>
            <Box padding="200">Delete selected rule.</Box>
          </>
        }
      />
    </>
  );
};

export default Table;
